import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { CHESS_API, uciToInput, type Chess } from '@chess/api-chess';
import { ENGINE_API, type Engine } from '@chess/api-engine';
import { REVIEW_API, type Review, type ReviewSession } from '@chess/api-review';
import { LOG_API, type Log } from '@chess/api-log';
import { ANALYSIS_API, type Analysis } from '@chess/api-analysis';
import { CONSTRAINTS_API, type ConstraintRegistry } from '@chess/api-constraints';
import type { Uci } from '@chess/api-model';
import './shortlist.css';
import { createShortlistSearch, INITIAL_SHORTLIST, readingOf, type ShortlistSearch, type ShortlistState } from './search.ts';
import { watchNeed } from './need.ts';
import { formatScore } from './verdict.ts';
import { depthLabel, messageOf, playedScoreText, subtitleOf, verdictWords } from './text.ts';

// The shortlist: the engine's best three moves from the position on the
// board, scored for the side to move, and under them the move that was
// played from here, coloured green when it is the engine's choice and red
// when it is a blunder: a pawn or more of the score given up.
//
// Whether this panel or the theory list fills the best-moves place is the
// review session's `theory` decision, which `review-shortlist-theory` draws
// as the place's container; this panel renders inside it through
// `review.shortlist.engine`. While the theory shows, this panel sends the
// board no engine best move; the board draws the game variation's move or
// the scan's best instead, with the played move. At the initial position it
// renders nothing.
//
// The search itself is `search.ts`; the verdicts `verdict.ts`; the score
// bar's need `need.ts`; the words `text.ts`. This file is the panel.

interface PanelState extends ShortlistState {
  /** The best-moves place shows the theory: no engine best-move arrow. */
  theoryActive?: boolean;
}

const useShortlist = create<PanelState>(() => ({ ...INITIAL_SHORTLIST }));

export function setup(app: PiletApi) {
  let log: Log | undefined;
  void app.getApi<Log>(LOG_API).then((l) => (log = l.child(app.meta.name)));
  // Picking a line plays its first move through the review: the game's own
  // move steps forward, any other starts or extends the reader's line.
  let reviewApi: Review | undefined;
  void app.getApi<Review>(REVIEW_API).then((r) => (reviewApi = r));
  const pick = (uci: Uci) => {
    const ok = reviewApi?.play(uciToInput(uci)) ?? false;
    if (!ok) log?.warn('shortlist move could not be played', { uci });
  };

  // Tell the board what to draw: the best line's move and the played move.
  // In theory the book's moves replace the engine's: the board takes its green arrow from the book or the scan.
  useShortlist.subscribe((s) => app.emit('review.shortlist', readingOf(s, s.theoryActive)));

  // Whether the theory shows is the review session's decision; it only decides the arrow here.
  void app.getApi<ReviewSession>(REVIEW_API).then((session) => {
    useShortlist.setState({ theoryActive: session.state().theory?.active });
    session.select((st) => st.theory?.active, (active) => useShortlist.setState({ theoryActive: active }));
  });

  const search: Promise<ShortlistSearch> = Promise.all([
    app.getApi<Review>(REVIEW_API),
    app.getApi<Engine>(ENGINE_API),
    app.getApi<Chess>(CHESS_API),
    app.getApi<Analysis>(ANALYSIS_API),
  ]).then(([review, engine, chess, analysis]) =>
    createShortlistSearch({
      review,
      engine,
      chess,
      analysis,
      onState: (s) => useShortlist.setState(s),
      onError: (error) => log?.error('shortlist analysis failed', error instanceof Error ? error : { error: String(error) }),
    }),
  );
  // Holding the search once its APIs are in; letting go before then cancels the hold.
  const acquire = (): (() => void) => {
    let release: (() => void) | undefined;
    let alive = true;
    void search.then((s) => {
      if (alive) release = s.acquire();
    });
    return () => {
      alive = false;
      release?.();
    };
  };

  // The score bar's need: visible on the active board while the best moves are
  // hidden (the panel then is not mounted to hold the reading itself).
  void Promise.all([app.getApi<ReviewSession>(REVIEW_API), app.getApi<ConstraintRegistry>(CONSTRAINTS_API)]).then(
    ([session, registry]) => {
      const need = watchNeed(registry, acquire);
      need.watch(session.state().scope);
      session.select((st) => st.scope, need.watch);
    },
  );

  const Panel: React.FC = () => {
    const state = useShortlist();
    const { depth, candidates, played, status, fen, initial } = state;

    React.useEffect(() => acquire(), []);

    // Whether this panel or the theory list shows is the container's decision
    // (review-shortlist-theory owns `review.shortlist` and renders this one in
    // `review.shortlist.engine` when the position is not theory).
    if (initial) return null;

    const subtitle = subtitleOf(state);
    const message = messageOf(state);

    return (
      <section className="panel review-shortlist">
        <div className="shortlist-head" title={subtitle}>
          <h2>Best moves</h2>
          {subtitle && <span className="shortlist-subtitle">{depthLabel(depth)}</span>}
        </div>
        {message && <p className="panel-empty">{message}</p>}
        {candidates.length > 0 && (
          <ol className="shortlist-lines">
            {candidates.map((c) => (
              <li key={c.rank}>
                <button type="button" className="shortlist-line" data-track="review.shortlist.line" data-track-value={String(c.rank)} title={`Play ${c.san} on the board`} onClick={() => pick(c.uci)}>
                  <span className="shortlist-rank">{c.rank}</span>
                  <span className="shortlist-move">{c.san}</span>
                  <span className="shortlist-score">{formatScore(c.score)}</span>
                  <span className="shortlist-continuation">{c.continuation}</span>
                </button>
              </li>
            ))}
          </ol>
        )}
        {fen && played && (
          // The played move is a target like the engine's lines: picking it plays
          // the game's own move, which the review treats as stepping forward.
          <button
            type="button"
            className={`shortlist-played${played.verdict ? ` shortlist-played-${played.verdict}` : ''}`}
            title={`Play ${played.san} on the board, the game’s move`}
            data-track="review.shortlist.played"
            onClick={() => pick(played.uci)}>
            <span className="shortlist-rank">▶</span>
            <span className="shortlist-move">{played.san}</span>
            <span className="shortlist-score">{playedScoreText(played, status)}</span>
            <span className="shortlist-verdict">played in the game — {verdictWords(played.verdict, status)}</span>
          </button>
        )}
        {fen && !played && (
          <div className="shortlist-played">
            <span className="shortlist-rank">▶</span>
            <span className="shortlist-verdict">nothing was played from here</span>
          </div>
        )}
      </section>
    );
  };

  app.registerExtension('review.shortlist.engine', Panel);
}
